/**
 * PlayerConfiguration.js
 * Configure players for the game (add/remove, human/AI toggle, model selection)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Grid,
    Chip,
    Alert
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Person as PersonIcon,
    SmartToy as SmartToyIcon
} from '@mui/icons-material';
import gameManagementApi from './gameManagementApi';

// Default models for the first 8 players (in order)
// After the 8th player, additional players will cycle through this list
const DEFAULT_MODEL_SEQUENCE = [
    // Free-tier defaults only — premium (🔒) models become selectable for a fee later
    'x-ai/grok-4.3',                        // 1. Grok 4.3 (fast agentic)
    'anthropic/claude-haiku-4.5',            // 2. Haiku 4.5 (fast, cost-effective)
    'google/gemini-2.5-flash',              // 3. Gemini Flash
    'openai/gpt-5-mini',                    // 4. GPT 5 Mini
    'deepseek/deepseek-chat-v3.1',          // 5. DeepSeek 3.1
    'qwen/qwen3-235b-a22b-2507',            // 6. Qwen 3
    'moonshotai/kimi-k2-0905',              // 7. Kimi K2
    'meta-llama/llama-3.3-70b-instruct'     // 8. Llama
];

// Get model for a player based on their position
const getModelForPlayer = (playerIndex) => {
    return DEFAULT_MODEL_SEQUENCE[playerIndex % DEFAULT_MODEL_SEQUENCE.length];
};

export default function PlayerConfiguration({ gameTypeDetails, players, onChange }) {
    const [localPlayers, setLocalPlayers] = useState(players);
    const [aiModels, setAiModels] = useState([]);
    const [modelsLoading, setModelsLoading] = useState(false);
    const debounceTimer = useRef(null);

    useEffect(() => {
        loadAiModels();
    }, []);

    useEffect(() => {
        setLocalPlayers(players);
    }, [players]);


    const loadAiModels = async () => {
        try {
            setModelsLoading(true);
            const result = await gameManagementApi.getAvailableModels();
            if (result.success) {
                setAiModels(result.models);
            }
        } catch (error) {
            console.error('[PlayerConfiguration] Failed to load AI models:', error);
        } finally {
            setModelsLoading(false);
        }
    };

    // Debounced update to parent - only update after 300ms of no changes
    const debouncedOnChange = useCallback((updatedPlayers) => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        debounceTimer.current = setTimeout(() => {
            onChange(updatedPlayers);
        }, 300);
    }, [onChange]);

    // Immediate update to parent (for structural changes like add/remove)
    const immediateOnChange = useCallback((updatedPlayers) => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        onChange(updatedPlayers);
    }, [onChange]);

    const handleAddPlayer = () => {
        const newPlayer = {
            id: `player-${Date.now()}`,
            name: `Player ${localPlayers.length + 1}`,
            is_human: false,
            fake_being_human: false, // Default to false for alignment research toggle
            model: getModelForPlayer(localPlayers.length), // Use sequential model selection
            ...Object.keys(gameTypeDetails.playerSchema).reduce((acc, key) => {
                if (gameTypeDetails.playerSchema[key].default !== undefined) {
                    acc[key] = gameTypeDetails.playerSchema[key].default;
                }
                return acc;
            }, {})
        };

        const updated = [...localPlayers, newPlayer];
        setLocalPlayers(updated);
        immediateOnChange(updated); // Immediate for structural changes
    };

    const handleRemovePlayer = (index) => {
        const updated = localPlayers.filter((_, i) => i !== index);
        setLocalPlayers(updated);
        immediateOnChange(updated); // Immediate for structural changes
    };

    const handlePlayerChange = (index, field, value) => {
        const updated = [...localPlayers];
        updated[index] = { ...updated[index], [field]: value };
        setLocalPlayers(updated);
        debouncedOnChange(updated); // Debounced for text input
    };

    const handleToggleHuman = (index) => {
        const updated = [...localPlayers];
        updated[index] = { ...updated[index], is_human: !updated[index].is_human };
        setLocalPlayers(updated);
        immediateOnChange(updated); // Immediate for toggle
    };

    const renderPlayerField = (player, index, key, fieldSchema) => {
        const value = player[key] || '';

        switch (fieldSchema.type) {
            case 'string':
            case 'text':
                return (
                    <TextField
                        key={key}
                        fullWidth
                        label={fieldSchema.label || key}
                        value={value}
                        onChange={(e) => handlePlayerChange(index, key, e.target.value)}
                        size="small"
                        required={fieldSchema.required}
                        sx={{ mb: 2 }}
                    />
                );

            case 'textarea':
                return (
                    <TextField
                        key={key}
                        fullWidth
                        multiline
                        rows={3}
                        label={fieldSchema.label || key}
                        value={value}
                        onChange={(e) => handlePlayerChange(index, key, e.target.value)}
                        size="small"
                        required={fieldSchema.required}
                        sx={{ mb: 2 }}
                    />
                );

            default:
                return null;
        }
    };

    const canAddMorePlayers = localPlayers.length < gameTypeDetails.maxPlayers;
    const canRemovePlayers = localPlayers.length > gameTypeDetails.minPlayers;

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Configure Players
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Add players to the game. You can mix human and AI players.
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2">
                    Players: {localPlayers.length} / {gameTypeDetails.maxPlayers}
                    {' '}(minimum: {gameTypeDetails.minPlayers})
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddPlayer}
                    disabled={!canAddMorePlayers}
                >
                    Add Player
                </Button>
            </Box>

            {localPlayers.length < gameTypeDetails.minPlayers && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    You need at least {gameTypeDetails.minPlayers} players to start the game.
                </Alert>
            )}

            <Grid container spacing={2}>
                {localPlayers.map((player, index) => (
                    <Grid item xs={12} md={6} key={player.id}>
                        <Paper elevation={2} sx={{ p: 2, position: 'relative' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {player.is_human ? <PersonIcon color="primary" /> : <SmartToyIcon color="action" />}
                                    <Typography variant="h6">
                                        Player {index + 1}
                                    </Typography>
                                    <Chip
                                        label={player.is_human ? 'Human' : 'AI'}
                                        size="small"
                                        color={player.is_human ? 'primary' : 'default'}
                                    />
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={() => handleRemovePlayer(index)}
                                    disabled={!canRemovePlayers}
                                    color="error"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={player.is_human}
                                        onChange={() => handleToggleHuman(index)}
                                    />
                                }
                                label="Human Player"
                                sx={{ mb: 2 }}
                            />

                            {!player.is_human && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={player.fake_being_human || false}
                                            onChange={() => handlePlayerChange(index, 'fake_being_human', !player.fake_being_human)}
                                        />
                                    }
                                    label="Fake Being Human (Alignment Research)"
                                    sx={{ mb: 2 }}
                                />
                            )}

                            <TextField
                                fullWidth
                                label="Player Name"
                                value={player.name}
                                onChange={(e) => handlePlayerChange(index, 'name', e.target.value)}
                                required
                                size="small"
                                sx={{ mb: 2 }}
                            />

                            {!player.is_human && (() => {
                                const dropdownValue = aiModels.length === 0 ? '' : (aiModels.some(m => m.id === player.model) ? player.model : '');
                                return (
                                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                    <InputLabel>AI Model</InputLabel>
                                    <Select
                                        value={dropdownValue}
                                        label="AI Model"
                                        onChange={(e) => handlePlayerChange(index, 'model', e.target.value)}
                                        disabled={modelsLoading}
                                    >
                                        {aiModels.map((model) => (
                                            <MenuItem key={model.id} value={model.id} disabled={!!model.premium}>
                                                {model.name}
                                                {model.recommended && !model.premium && ' ⭐'}
                                                {model.premium && ' 🔒 Premium — coming soon'}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                );
                            })()}

                            {/* Render additional fields from playerSchema */}
                            {Object.entries(gameTypeDetails.playerSchema)
                                .filter(([key]) => !['name', 'is_human', 'model'].includes(key))
                                .map(([key, fieldSchema]) => renderPlayerField(player, index, key, fieldSchema))}
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}
